import { DEFAULT_GRADIENT } from './gradient';
import { BrushState, Tool, ToolId } from './types';
import { DEFAULT_WIDGET_OPTIONS, widgetTool } from './widgets';
import {
  arrowTool,
  boxTool,
  brushTool,
  ellipseTool,
  eraserTool,
  eyedropperTool,
  fillTool,
  gradientTool,
  lassoTool,
  lineTool,
  moveTool,
  pencilTool,
  rectTool,
  selectTool,
  wandTool,
} from './drawTools';
import {
  brailleEllipseTool,
  brailleLineTool,
  brailleRectTool,
  brailleTool,
} from './brailleTools';
import { Attr } from '../core/types';

/**
 * The text tool is driven by the editor store rather than the stroke pipeline
 * (it owns a caret and consumes key presses), so it is declared here for the
 * toolbar but has no `plan`.
 */
export const textTool: Tool = {
  id: 'text',
  name: 'Text',
  group: 'draw',
  shortcut: 'T',
  hint: 'Click to place a caret and type. Enter starts a new line at the origin column.',
};

export const TOOLS: readonly Tool[] = [
  selectTool,
  lassoTool,
  wandTool,
  moveTool,
  pencilTool,
  brushTool,
  eraserTool,
  textTool,
  lineTool,
  rectTool,
  ellipseTool,
  boxTool,
  arrowTool,
  fillTool,
  gradientTool,
  brailleTool,
  brailleLineTool,
  brailleRectTool,
  brailleEllipseTool,
  widgetTool,
  eyedropperTool,
];

const BY_ID = new Map<ToolId, Tool>(TOOLS.map((t) => [t.id, t]));

export function getTool(id: ToolId): Tool {
  return BY_ID.get(id) ?? pencilTool;
}

export function toolByShortcut(key: string): Tool | undefined {
  const upper = key.toUpperCase();
  return TOOLS.find((t) => t.shortcut === upper);
}

export const DEFAULT_BRUSH: BrushState = {
  char: '█',
  fg: 0x32ff94,
  bg: 0x06070a,
  attrs: Attr.NONE,
  size: 1,
  paintChar: true,
  paintFg: true,
  paintBg: true,
  lineStyle: 'light',
  filled: false,
  gradient: DEFAULT_GRADIENT,
  widgetOptions: DEFAULT_WIDGET_OPTIONS,
  brailleErase: false,
  sampleComposite: false,
  tolerance: 0,
};

/** Tools that need a Braille-aware cursor and sub-cell pointer tracking. */
export const BRAILLE_TOOLS: ReadonlySet<ToolId> = new Set([
  'braille',
  'brailleLine',
  'brailleRect',
  'brailleEllipse',
]);
